/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable react/jsx-max-depth */
import { Loader } from '@transferwise/components';
import { formatMoney } from '@transferwise/formatting';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Disclaimer from '../common/Disclaimer';
import ErrorTemplate from '../common/ErrorTemplate';
import { TapestryCard } from '../common/tapestrycard/TapestryCard';
import useDebounce from '../resources/useDebounce';

import style from './TravelMoney.module.less';
import Options from './components/Options';
import ProviderRow from './components/ProviderRow';
import useTravelMoneyApi, { STATUS } from './lib/useTravelMoneyApi';

function createClassNames(classNames) {
  return classNames.map((className) => `${style[className]}`).join(' ');
}

const SEND_AMOUNT_CHANGE_DEBOUNCE_MS = 350;

const TravelMoney = ({
  initialSendAmount,
  initialSourceCurrency,
  initialTargetCurrency,
  initialData,
  ctaUrlSavings,
  ctaUrlNotCheapest,
  track: trackFn,
}) => {
  const { locale } = useIntl();
  const [sendAmount, setSendAmount] = useState(initialSendAmount);
  const [sourceCurrency, setSourceCurrency] = useState(initialSourceCurrency);
  const [targetCurrency, setTargetCurrency] = useState(initialTargetCurrency);

  // A version of sendAmount that doesn't update immediately
  // Used to trigger fetch for new data when the user stops typing
  const debouncedSendAmount = useDebounce(sendAmount, SEND_AMOUNT_CHANGE_DEBOUNCE_MS);

  const { providers, midMarketRate, twSavings, status, errorType } = useTravelMoneyApi(
    debouncedSendAmount,
    sourceCurrency,
    targetCurrency,
    initialData,
  );

  const track = (event) =>
    trackFn({
      event,
      sendAmount,
      sourceCurrency,
      targetCurrency,
      isTransferWiseCheapest: twSavings > 0,
      savings: twSavings,
      providers: providers.map((p) => p.alias),
    });

  useEffect(() => {
    if (status !== STATUS.RESOLVED) {
      return;
    }
    track('Travel Money - View Comparison');
  }, [providers]);

  const onRouteChange = (event) => {
    if (event.sourceCurrency) {
      setSourceCurrency(event.sourceCurrency);
    }
    if (event.targetCurrency) {
      setTargetCurrency(event.targetCurrency);
    }
  };

  return (
    <div
      className={createClassNames(['comparison-widget', 'comparison-widget--wide', 'travel-money'])}
    >
      <TapestryCard>
        <div className={style.card}>
          <div className={style['card-header']}>
            <Options
              sourceCurrency={sourceCurrency}
              targetCurrency={targetCurrency}
              onRouteChange={onRouteChange}
              amount={sendAmount}
              onAmountChange={setSendAmount}
            />
          </div>
          {status === STATUS.PENDING && (
            <div
              className={createClassNames(['d-flex', 'justify-content-center', 'p-t-5', 'p-b-5'])}
            >
              <Loader />
            </div>
          )}
          {status === STATUS.RESOLVED && (
            <div className={style['card-provider-table']}>
              <table className={style['table-plain']}>
                <caption className={style['table-plain__caption']}>
                  <FormattedMessage id="table-caption" />
                </caption>
                <thead className={createClassNames(['thead'])}>
                  <tr className={style['table-plain__row']}>
                    <th className={style['table-plain__th']}>
                      <FormattedMessage id="provider" />
                    </th>
                    <th className={style['table-plain__th']}>
                      <FormattedMessage id="exchange-rate" />
                    </th>
                    <th className={style['table-plain__th']}>
                      <FormattedMessage id="delivery-fee" />
                    </th>
                    <th className={style['table-plain__th']}>
                      <FormattedMessage id="should-arrive" />
                    </th>
                    <th className={createClassNames(['table-plain__th', 'featured--th'])}>
                      <FormattedMessage id="will-cost-you" />
                      <span className={createClassNames(['table-plain__th--small'])}>
                        <FormattedMessage
                          values={{
                            sendAmount: formatMoney(sendAmount, sourceCurrency, locale),
                          }}
                          id="when-buying"
                        />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((provider) => (
                    <ProviderRow
                      key={provider.id}
                      sourceCurrency={sourceCurrency}
                      targetCurrency={targetCurrency}
                      midMarketRate={midMarketRate}
                      twSavings={twSavings}
                      ctaUrlSavings={ctaUrlSavings}
                      ctaUrlNotCheapest={ctaUrlNotCheapest}
                      track={track}
                      {...provider}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {status === STATUS.REJECTED && (
            <ErrorTemplate
              source={sourceCurrency}
              errorType={errorType}
              createClassNames={createClassNames}
              style={style}
            />
          )}
        </div>
      </TapestryCard>

      <Disclaimer
        style={style}
        createClassNames={createClassNames}
        sourceCurrency={sourceCurrency}
        targetCurrency={targetCurrency}
      />
    </div>
  );
};

TravelMoney.propTypes = {
  initialSendAmount: PropTypes.number.isRequired,
  initialSourceCurrency: PropTypes.string.isRequired,
  initialTargetCurrency: PropTypes.string.isRequired,
  initialData: PropTypes.shape(),
  ctaUrlSavings: PropTypes.string,
  ctaUrlNotCheapest: PropTypes.string,
  track: PropTypes.func,
};

TravelMoney.defaultProps = {
  initialData: null,
  ctaUrlSavings: '#savings',
  ctaUrlNotCheapest: '/gb/multi-currency-account',
  track: () => {},
};

export default TravelMoney;
